import type {
  WorkbookPivotGroup,
  WorkbookPivotSpec,
  WorkbookPivotValue,
} from "@erp/shared-types";
import type { ColumnType } from "../formula";

/**
 * How a pivot's Result is laid out as a table, the rule every downstream
 * consumer (Worksheets, Reports, Files) reads it by:
 *
 * 1. One Text column per Row field, named after its Field, or `Date (Month)`
 *    when date grouped. Each row repeats its labels (a spreadsheet's tabular
 *    form); an Empty group's label is Empty.
 * 2. Then one Number column per Value, named after it (`SUM of Net`) — or,
 *    with Column fields, one per Column key and Value, named after the key's
 *    labels joined by ` - ` (`East - Ann`), followed by ` - ` and the Value's
 *    name when there are several Values; an Empty label reads `(Empty)`.
 *    Grand Total columns (`Grand Total`, or `Grand Total - SUM of Net`) follow.
 * 3. A name taken already (ignoring case) gets ` (2)`, ` (3)`… in order.
 * 4. Rows: a group's subtotal row reads `<label> Total` in its Field's column
 *    and Empty in the deeper ones; the Grand Total row reads `Grand Total` in
 *    the first.
 *
 * Column keys come from the data unless pinned (`columnKeys`); only pinned
 * ones give names that never change with the data, so other Worksheets can
 * read only those columns by name.
 */
export interface PivotOutputColumn {
  name: string;
  type: ColumnType;
  /** A shown-as Value's percentage format, for the export. */
  numberFormat?: string;
}

export const GRAND_TOTAL = "Grand Total";
export const EMPTY_LABEL = "(Empty)";
export const KEY_SEPARATOR = " - ";
export const PERCENT_FORMAT = "0.00%";

const SUMMARY_NAMES: Readonly<Record<WorkbookPivotValue["summarize"], string>> =
  {
    sum: "SUM",
    count: "COUNT",
    counta: "COUNTA",
    countunique: "COUNTUNIQUE",
    average: "AVERAGE",
    min: "MIN",
    max: "MAX",
    median: "MEDIAN",
  };
const SHOW_AS_NAMES: Readonly<
  Record<Exclude<WorkbookPivotValue["showAs"], "value">, string>
> = {
  "percent-of-grand-total": "% of grand total",
  "percent-of-row": "% of row",
  "percent-of-column": "% of column",
};
const DATE_GROUP_NAMES: Readonly<
  Record<NonNullable<WorkbookPivotGroup["dateGroup"]>, string>
> = { year: "Year", quarter: "Quarter", month: "Month", day: "Day" };

/** A Row field's column name. */
export function pivotGroupName(group: WorkbookPivotGroup): string {
  return group.dateGroup
    ? `${group.field} (${DATE_GROUP_NAMES[group.dateGroup]})`
    : group.field;
}

/** A Value's name: its own, else like `SUM of Net` or `SUM of Net (% of row)`. */
export function pivotValueName(value: WorkbookPivotValue): string {
  if (value.name) return value.name;
  const base = `${SUMMARY_NAMES[value.summarize]} of ${value.field}`;
  return value.showAs === "value"
    ? base
    : `${base} (${SHOW_AS_NAMES[value.showAs]})`;
}

/** Appends ` (2)`, ` (3)`… to names taken already, ignoring case. */
export function uniqueNames(names: readonly string[]): string[] {
  const taken = new Set<string>();
  return names.map((name) => {
    let candidate = name;
    for (let suffix = 2; taken.has(candidate.toLowerCase()); suffix++)
      candidate = `${name} (${suffix})`;
    taken.add(candidate.toLowerCase());
    return candidate;
  });
}

function valueColumn(
  value: WorkbookPivotValue,
  name: string,
): PivotOutputColumn {
  return value.showAs === "value"
    ? { name, type: "decimal" }
    : { name, type: "decimal", numberFormat: PERCENT_FORMAT };
}

/** One entry per value column: which Column key (null: Grand Total) and Value it holds. */
export interface PivotValueSlot {
  key: readonly string[] | null;
  value: number;
}

/** The value columns' slots, in output order. */
export function pivotValueSlots(
  spec: WorkbookPivotSpec,
  columnKeys: readonly (readonly string[])[],
): PivotValueSlot[] {
  const values = spec.values.map((_, index) => index);
  if (!spec.columns.length)
    return values.map((value) => ({ key: null, value }));
  const slots: PivotValueSlot[] = columnKeys.flatMap((key) =>
    values.map((value) => ({ key, value })),
  );
  if (spec.grandTotals.columns)
    slots.push(...values.map((value) => ({ key: null, value })));
  return slots;
}

function slotName(spec: WorkbookPivotSpec, slot: PivotValueSlot): string {
  const value = spec.values[slot.value];
  const name = value ? pivotValueName(value) : "";
  if (!spec.columns.length) return name;
  const label = slot.key ? slot.key.join(KEY_SEPARATOR) : GRAND_TOTAL;
  return spec.values.length === 1 ? label : `${label}${KEY_SEPARATOR}${name}`;
}

/**
 * A pivot's output columns for the given Column keys: Row fields, then value
 * columns, named by the layout rule above.
 */
export function pivotOutputColumns(
  spec: WorkbookPivotSpec,
  columnKeys: readonly (readonly string[])[],
): PivotOutputColumn[] {
  const slots = pivotValueSlots(spec, columnKeys);
  const names = uniqueNames([
    ...spec.rows.map(pivotGroupName),
    ...slots.map((slot) => slotName(spec, slot)),
  ]);
  return [
    ...spec.rows.map((_, index) => ({
      name: names[index] ?? "",
      type: "text" as const,
    })),
    ...slots.map((slot, index) => {
      const value = spec.values[slot.value];
      const name = names[spec.rows.length + index] ?? "";
      return value
        ? valueColumn(value, name)
        : { name, type: "decimal" as const };
    }),
  ];
}

/**
 * The output columns known without data: all of them when the pivot has no
 * Column fields or pins its Column keys; else the Row fields and Grand Total
 * columns (`dynamic`: the rest come from the data).
 */
export function pivotStaticColumns(spec: WorkbookPivotSpec): {
  columns: PivotOutputColumn[];
  dynamic: boolean;
} {
  if (!spec.columns.length || spec.columnKeys)
    return {
      columns: pivotOutputColumns(spec, spec.columnKeys ?? []),
      dynamic: false,
    };
  return { columns: pivotOutputColumns(spec, []), dynamic: true };
}

/**
 * The Declared types (and percentage formats) of a pivot Result's columns,
 * by position: its Row fields' Text columns, then value columns, which cycle
 * through the Values in order whatever Column keys the data spread them into.
 */
export function pivotResultColumns(
  spec: WorkbookPivotSpec,
  columnNames: readonly string[],
): PivotOutputColumn[] {
  return columnNames.map((name, position) => {
    if (position < spec.rows.length) return { name, type: "text" };
    const value =
      spec.values[
        (position - spec.rows.length) % Math.max(spec.values.length, 1)
      ];
    return value ? valueColumn(value, name) : { name, type: "decimal" };
  });
}
